import { ThunkDispatch } from "core-integration/src/store";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getSquadError } from "core-integration/src/store/squad/reducers";
import { createEntry } from "core-integration/src/store/squad/thunks";
import { ICreateEntryData } from "core-integration/src/store/squad/types";
import { getTeams } from "core-integration/src/store/teams/reducers";
import React, { useState } from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import Alert from "../Alert";
import Button from "../Button";
import Dialog from "../Dialog";
import DialogHeading from "../DialogHeading";
import {
  CheckboxField,
  FieldWrap,
  InputField,
  SelectField,
} from "../FieldRenderers";
import { getCodeFromError } from "./helpers";

interface IOwnProps {
  handleHide: () => void;
  allowNavigation: () => void;
}

interface IState extends ICreateEntryData {
  favourite_team: number;
  least_favourite_team: number;
}

type Props = IOwnProps & WithTranslation;

const CreateEntryDialog: React.FC<Props> = ({
  allowNavigation,
  handleHide,
  i18n,
  t,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const [state, setState] = useState<IState>({
    email: false,
    favourite_team: 0,
    least_favourite_team: 0,
    name: "",
    terms_agreed: false,
    language: i18n.language,
  });

  const error = useSelector(getSquadError);
  const nextEvent = useSelector(getNextEvent);
  const teams = useSelector(getTeams);

  const requiredFields: Array<keyof IState> = [
    "favourite_team",
    "name",
    "terms_agreed",
    "language",
  ];

  const apiDataFromState = () => ({
    email: state.email,
    favourite_team: state.favourite_team > 0 ? state.favourite_team : null,
    least_favourite_team:
      state.least_favourite_team > 0 ? state.least_favourite_team : null,
    name: state.name,
    terms_agreed: state.terms_agreed,
    language: i18n.language,
  });

  const handleEmailChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, email: e.currentTarget.checked ? true : false });

  const handleFaveChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, favourite_team: parseInt(e.currentTarget.value, 10) });

  const handleLeastFaveChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({
      ...state,
      least_favourite_team: parseInt(e.currentTarget.value, 10),
    });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleTermsChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      terms_agreed: e.currentTarget.checked ? true : false,
    });

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isValid()) {
      await dispatch(createEntry(apiDataFromState()));
      allowNavigation();
      navigate("/my-team");
    }
  };

  const isValid = () => requiredFields.every((e) => Boolean(state[e]));

  // filterTeams
  const spl = teams
    .filter((team) => team.unavailable === false)
    .sort((a, b) => a.name.localeCompare(b.name));

  const errorCode = getCodeFromError(error);
  if (error) {
    const handledErrorCodes = ["price_changed"];
    if (handledErrorCodes.indexOf(errorCode) === -1) {
      window.location.reload();
      return null;
    }
  }
  if (!nextEvent) {
    return (
      <Dialog closeDialog={handleHide}>
        <Dialog.Header closeDialog={handleHide}>
          {t("createEntryDialog.over", "Game over")}
        </Dialog.Header>
        <Dialog.Body isPadded={true}>
          <p>
            {t(
              "createEntryDialog.finished",
              "This game has now finished. Thanks for your interest, come back soon to select your squad for next season's game.",
            )}
          </p>
        </Dialog.Body>
      </Dialog>
    );
  }

  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <DialogHeading>
          {t("createEntryDialog.enterSquad", "Enter squad")}
        </DialogHeading>
      </Dialog.Header>

      <Dialog.Body isPadded={true}>
        <form onSubmit={handleSubmit}>
          <FieldWrap>
            <InputField
              id="entryName"
              name="entryName"
              label={t("createEntryDialog.teamName", "Team Name")}
              hint={t(
                "createEntryDialog.maxCharacters",
                "Maximum 20 characters",
              )}
              onChange={handleNameChange}
              value={state.name}
              maxLength={20}
            />
          </FieldWrap>
          <FieldWrap>
            <SelectField
              id="entryFave"
              name="entryFave"
              label={t("createEntryDialog.favouriteClub", "Favourite Club")}
              onChange={handleFaveChange}
              value={state.favourite_team}
            >
              <option value={0} aria-selected={state.favourite_team === 0}>
                {t("createEntryDialog.chooseAClub", "Choose a club")}...
              </option>

              <optgroup label={t<string>("topLeague", "SPL")}>
                {spl.map((tm) => (
                  <option
                    value={tm.id}
                    key={tm.id}
                    aria-selected={state.favourite_team === tm.id}
                  >
                    {tm.name}
                  </option>
                ))}
              </optgroup>

              <option value={-1} aria-selected={state.favourite_team === -1}>
                {t("createEntryDialog.none", "None")}
              </option>
            </SelectField>
          </FieldWrap>
          <FieldWrap>
            <SelectField
              id="entryLeastFave"
              name="entryLeastFave"
              label={t(
                "createEntryDialog.leastFavouriteClubn",
                "Secondary Club",
              )}
              required={false}
              onChange={handleLeastFaveChange}
              value={state.least_favourite_team}
            >
              <option
                value={0}
                aria-selected={state.least_favourite_team === 0}
              >
                {t("createEntryDialog.chooseAClubLeast", "Choose a club")}...
              </option>

              <optgroup label={t<string>("topLeague", "SPL")}>
                {spl.map((tm) => (
                  <option
                    value={tm.id}
                    key={tm.id}
                    aria-selected={state.least_favourite_team === tm.id}
                  >
                    {tm.name}
                  </option>
                ))}
              </optgroup>

              <option
                value={-1}
                aria-selected={state.least_favourite_team === -1}
              >
                {t("createEntryDialog.none", "None")}
              </option>
            </SelectField>
          </FieldWrap>
          <FieldWrap>
            <CheckboxField
              id="entryEmail"
              name="entryEmail"
              label={t(
                "createEntryDialog.text",
                "I'd like to get email updates about my fantasy team.",
              )}
              onChange={handleEmailChange}
              checked={state.email}
            />
          </FieldWrap>
          <FieldWrap>
            <CheckboxField
              id="entryTerms"
              name="entryTerms"
              label={
                <Trans
                  i18nKey="createEntryDialog.terms"
                  defaults="I'm over the age of 15 and agree to the SPL <1>Terms and conditions.</1>"
                  components={{ 1: <Link to="/help/terms" /> }}
                >
                  I'm over the age of 15 and agree to the SPL{" "}
                  <Link to="/help/terms">Terms and conditions.</Link>
                </Trans>
              }
              onChange={handleTermsChange}
              checked={state.terms_agreed}
            />
          </FieldWrap>
          {errorCode && (
            <Box mb={2}>
              <Alert type="error">
                {errorCode === "price_changed" &&
                  t(
                    "createEntryDialog.unable",
                    "Unable to enter squad as player prices have changed. Please refresh the page and try again.",
                  )}
              </Alert>
            </Box>
          )}
          <Button type="submit" disabled={!isValid()}>
            {t("createEntryDialog.enterSquad", "Enter squad")}
          </Button>
        </form>
      </Dialog.Body>
    </Dialog>
  );
};

export { CreateEntryDialog as CreateEntryDialogTest };

export default withTranslation()(CreateEntryDialog);
